/**
 * Collapse contract for the P&L grid toolbar.
 *
 * Measured on the toolbar ELEMENT, like the header: this row sits inside a
 * sidebar, a detail panel that opens and closes, and fullscreen mode, so the
 * viewport says nothing useful about the space it has.
 *
 * The order things are given up in, loudest need first:
 *   1. Words that repeat an icon ("Synced" next to a check).
 *   2. Long segment labels — "Actuals + Forecast" / "Forecast only" become
 *      "Actuals" / "Forecast", which still read as a pair.
 *   3. Button labels ("Bulk Edit", "Analyze with AI") → icon only, tooltip kept.
 *   4. Dividers.
 * The zoom, fullscreen and filter controls are already icons and never change,
 * and no control is ever removed — a toolbar that hides a button at 900px is a
 * feature the user cannot find.
 */

export type ToolbarTier = 'xl' | 'lg' | 'md' | 'sm';

export interface ToolbarLayout {
  tier: ToolbarTier;
  /** "Synced" / "Updated" wording next to the status check. */
  showSyncLabel: boolean;
  /** Full "Actuals + Forecast" / "Forecast only" vs the short pair. */
  showLongSegmentLabels: boolean;
  showBulkEditLabel: boolean;
  showAiLabel: boolean;
  showDividers: boolean;
}

/**
 * Breakpoints are for the bar as it is now: the setup controls (export,
 * columns, density, zoom, fullscreen) live behind ⋯, so the bar needs about
 * 900px for every label — measured at the ribbon slot, which is what is left
 * of the row once the property / period / version chips have taken theirs.
 */
export function toolbarTierFor(width: number): ToolbarTier {
  if (width >= 900) return 'xl';
  if (width >= 760) return 'lg';
  if (width >= 620) return 'md';
  return 'sm';
}

export function toolbarLayoutFor(width: number): ToolbarLayout {
  const tier = toolbarTierFor(width);
  return {
    tier,
    showSyncLabel: tier === 'xl' || tier === 'lg',
    showLongSegmentLabels: tier === 'xl',
    showBulkEditLabel: tier === 'xl' || tier === 'lg',
    showAiLabel: tier !== 'sm',
    showDividers: tier !== 'sm',
  };
}

/** Segment labels for the actuals/forecast toggle at the current width. */
export function segmentLabels(layout: ToolbarLayout): { blended: string; forecastOnly: string } {
  return layout.showLongSegmentLabels
    ? { blended: 'Actuals + Forecast', forecastOnly: 'Forecast only' }
    : { blended: 'Actuals', forecastOnly: 'Forecast' };
}
